import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import ApplyViewModel from './ApplyViewModel';
import { Appl } from '../../../../core/Appl';
const GettingStarted = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.GettingStarted, memberId!);
  }, []);

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Getting Started</h1>
          <p className="tracking--1percent">
            Welcome to the SCERS Member Portal! We've made it easy and convenient for you to start your retirement journey online.
          </p>
        </div>
        <section className="space-y-6 tracking--1percent text-black">
          <h2 className="font-semibold text-gray-900 text-22px">Ready to Apply? Here's What You Need</h2>
          <ul className="space-y-4 list-disc pl-6 leading-none">
            <li>
              <span className="font-semibold">Personal Information:</span> Provide your most up-to-date information.
            </li>
            <li>
              <span className="font-semibold">Beneficiary Information:</span> Be prepared to provide details for your beneficiaries.
            </li>
            <li>
              <span className="font-semibold">Required Documents:</span> You'll need to upload necessary documents, such as proof of age or marriage
              certificates.
            </li>
          </ul>
        </section>

        <section className="space-y-6">
          <h2 className="font-bold">Things to Consider Before Setting a Retirement Date:</h2>

          <ul className="space-y-6  list-disc ml-6">
            <li>
              <h3 className="font-semibold mb-2 text-black">Retirement Date Guidelines:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your retirement date must be the day after your last paid day of employment.</li>
                <li>It cannot be earlier than the date SCERS receives your application.</li>
                <li>Ensure you coordinate your retirement and resignation with your employer to ensure a smooth transition.</li>
              </ul>
            </li>

            <li>
              <h3 className="font-semibold mb-2 text-black">Age Factor Adjustments:</h3>
              <p className="text-black">
                The longer you work, the higher your retirement benefit. You'll receive an incremental age factor adjustment for each quarter of a
                year increase in your age, depending on your benefit tier.
              </p>
            </li>

            <li>
              <h3 className="font-semibold mb-2 text-black">Cost-of-Living Adjustment (COLA):</h3>
              <p className="text-black">
                The annual COLA takes effect on April 1st. To receive this adjustment, your retirement must be effective before this date. Keep in
                mind that the months leading up to April are high-volume, so processing your first retirement check might take longer during this
                period.
              </p>
            </li>
            <li>
              <h3 className="font-semibold mb-2 text-black">Service Credit Purchases:</h3>
              <p className="text-black">
                If you're purchasing additional service credit, make sure to allow enough time for SCERS to process your purchase before your
                retirement date.
              </p>
            </li>
            <li>
              <h3 className="font-semibold mb-2 text-black">Reciprocity:</h3>
              <p className="text-black">
                If you’re a member of another California public retirement system with which SCERS has reciprocity, you may be eligible for coordinated benefits. To receive the advantages of reciprocity, you must retire from all reciprocal systems on the same date and for the same reason. Be sure to notify SCERS early in the application process so your reciprocal information can be verified.
              </p>
            </li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="font-bold text-gray-900">For More Information</h2>
          <p className="text-black">
            To learn more about your retirement benefits or the application process, visit <span className="text-primary-500 underline" style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.scers.gov')}>www.scers.gov</span>. If you have questions or need help at any point, we’re here to assist you—don’t hesitate to reach out.
          </p>
        </section>
      </div>
    </>
  );
});

export default GettingStarted;
